import {
  OrderingSessionAlreadySubmittedError,
  OrderingSessionDiscardConflictError,
  OrderingSessionGoneError,
  parseDiscardConflictKind,
  RestartConflictError,
  RestartDraftExistsError,
} from '@/features/ordering/errors'
import type { CanonicalOrderPayload } from '@/ordering/canonicalOrder'
import { reminderRefusalFromDetail, type ReminderRefusal } from '@/pages/sign/reminderRefusal'
import * as Sentry from '@sentry/react'
import type { LaunchMode } from './launchContext'
import { EpicAuthClient } from './services/EpicAuthClient'

export {
  OrderingSessionAlreadySubmittedError,
  OrderingSessionDiscardConflictError,
  OrderingSessionGoneError,
} from '@/features/ordering/errors'

enum HTTPMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

interface RequestStructure {
  method: HTTPMethod
  path: string
  headers?: Record<string, string>
  body?: string | object
  signal?: AbortSignal
}

/**
 * Unified JWT API Client for Provider API
 *
 * This client automatically injects JWT tokens for all authenticated requests.
 * Supports both Epic SMART on FHIR launches and Auth0 standalone mode.
 *
 * Authentication Flow:
 * 1. Epic Launch: Uses JWT token from Epic OAuth flow (stored in EpicAuthClient)
 * 2. Standalone: Uses Auth0 JWT token (via getAccessTokenSilently)
 * 3. For authenticated requests, JWT is automatically fetched and injected
 * 4. Backend validates JWT and authorizes based on token claims
 */
/** Which token was used for the last provider-api request (for debugging / Network tab). */
export type ProviderAPIAuthSource = 'auth0' | 'epic-provision' | 'epic-api-key' | null

/** A server-rendered TRF: its bytes plus the `Content-Disposition` filename, when sent. */
export interface TrfPdf {
  bytes: Uint8Array
  filename: string | null
}

/** One address autosuggestion from GET /geo/suggestions. */
export interface AddressSuggestion {
  text: string
  placeId?: string | null
}

/** Request body for POST /ordering/sessions/{sessionId}/coverage-check (all fields optional). */
export interface CoverageCheckRequest {
  /** Epic FHIR access token (same one used for submit). Treat as a secret; HTTPS only. */
  ehrAccessToken?: string
  /**
   * The ordering/authorizing practitioner's NPI. Drives the `provider` dimension. The server no
   * longer derives this from the JWT user or the canonical order, so the UI must send it; if
   * omitted, `provider` comes back false (fail-closed).
   */
  authorizingNpi?: string
}

/**
 * One advisory coverage eligibility result. Iterate these generically — more
 * ``coverageType`` values and array entries will be added over time.
 */
export interface CoverageCheck {
  /** Coverage program identifier (e.g. "CCS" today). */
  coverageType: string
  /** Whether the patient qualifies for this coverage type. */
  patient: boolean
  /** Whether the encounter qualifies. */
  encounter: boolean
  /** Whether the ordering provider is eligible for this coverage type. */
  provider: boolean
}

/** Response from POST /ordering/sessions/{sessionId}/coverage-check (advisory; never blocks). */
export interface CoverageCheckResponse {
  /** Coverage types active for this tenant; [] when the feature is off. */
  enabledChecks: string[]
  /** One entry per enabled coverage type; [] when none enabled. */
  checks: CoverageCheck[]
}

/** Structured address resolved from GET /geo/place/{placeId} (kit address shape). */
export interface ResolvedAddress {
  street?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  country?: string | null
  label?: string | null
}

/**
 * Thrown by {@link ProviderAPIClient.sendTrfSignatureReminder} when the server
 * returns 409, carrying *which* refusal it was so the caller can tell one that
 * resolves on its own from one that never will. The status cannot say: the
 * endpoint uses 409 for an order that cannot be signed in the app, for an
 * envelope that does not exist yet, and for a link that has already gone out.
 */
export class SignatureReminderRefusedError extends Error {
  public readonly refusal: ReminderRefusal

  constructor(refusal: ReminderRefusal) {
    super(`TRF signature reminder refused: ${refusal}`)
    this.name = 'SignatureReminderRefusedError'
    this.refusal = refusal
  }
}

/**
 * Thrown by {@link ProviderAPIClient.createTrfSigningUrl} when the mint is
 * refused, carrying the HTTP status so the caller can tell a refusal that will
 * resolve on its own (the envelope is still being created) from one that never
 * will (the caller may not sign this order).
 *
 * `status` is null when the request never produced a response at all.
 */
export class TrfSigningUrlError extends Error {
  public readonly status: number | null

  constructor(status: number | null, detail: string) {
    super(`TRF signing URL mint failed: ${status ?? 'network'} ${detail}`)
    this.name = 'TrfSigningUrlError'
    this.status = status
  }
}

/** Answer of {@link ProviderAPIClient.getTrfSigner}. `signerEmail` is set only when `isSigner` is false. */
export interface TrfSigner {
  isSigner: boolean
  signerEmail: string | null
}

/**
 * Thrown by {@link ProviderAPIClient} when an Epic-launch provider-api call
 * cannot be re-authorized: the bounded, single-shot 401 re-exchange (a fresh
 * server-side `epic-provision` call) either could not obtain a token (e.g. the
 * re-`epic-provision` returned 503) or the retried call still 401'd.
 *
 * This is a TERMINAL signal — the SPA must surface a re-launch-from-Epic prompt
 * rather than re-calling `epic-provision` again. The re-exchange is never looped.
 */
export class EpicReExchangeRequiredError extends Error {
  constructor(message = 'Epic session could not be re-authorized; re-launch from Epic is required') {
    super(message)
    this.name = 'EpicReExchangeRequiredError'
  }
}

const abortError = () => new DOMException('The operation was aborted.', 'AbortError')

class ProviderAPIClient {
  private static baseURL: string = import.meta.env.VITE_PROVIDER_API_URL
  private getAccessToken?: () => Promise<string>
  private static instance: ProviderAPIClient | null = null
  private readonly orderingSessionVersions = new Map<string, string>()
  /** The last write queued per session; it never rejects. */
  private readonly orderingSessionWrites = new Map<string, Promise<void>>()
  /** Set after getAuthToken(); used to add X-Auth-Source header and Sentry so you can see which token was sent. */
  private lastAuthSource: ProviderAPIAuthSource = null

  private constructor(getAccessToken?: () => Promise<string>) {
    this.getAccessToken = getAccessToken
  }

  /** Returns which token type was used for the most recent auth (for debugging). */
  public getLastAuthSource(): ProviderAPIAuthSource {
    return this.lastAuthSource
  }

  /**
   * Get singleton instance with optional Auth0 token getter
   * @param getAccessToken - Function to get Auth0 access token
   */
  static getInstance(getAccessToken?: () => Promise<string>): ProviderAPIClient {
    if (!ProviderAPIClient.instance) {
      ProviderAPIClient.instance = new ProviderAPIClient(getAccessToken)
    } else if (getAccessToken) {
      // Update token getter if provided (for when Auth0 becomes available)
      ProviderAPIClient.instance.getAccessToken = getAccessToken
    }
    return ProviderAPIClient.instance
  }

  static resetInstance(): void {
    ProviderAPIClient.instance = null
  }

  /**
   * Set the Auth0 token getter function
   * @param getAccessToken - Function to get Auth0 access token
   */
  public setTokenGetter(getAccessToken: () => Promise<string>) {
    this.getAccessToken = getAccessToken
  }

  /**
   * Get authentication token for API requests.
   *
   * Priority:
   * 1. Epic launch: Auth0-compatible JWT from POST /auth/epic-provision (find user by NPI, get permissions)
   * 2. Standalone: Auth0 JWT token (getAccessTokenSilently)
   * 3. null (no authentication available)
   *
   * Returns BOTH the token and the source it was resolved from, so the caller
   * (`request`) can carry the source as a per-request local. The `X-Auth-Source`
   * header and the 401 re-exchange decision MUST be driven by this returned
   * value, NOT by the shared `lastAuthSource` instance field: `ProviderAPIClient`
   * is a singleton and `request` is plain async, so concurrent in-flight requests
   * interleave and a value read from `lastAuthSource` after an `await` may belong
   * to a different request (a standalone-Auth0 401 wrongly triggering an Epic
   * re-exchange, or an Epic 401 being skipped). `lastAuthSource` is still updated
   * for observability / `getLastAuthSource()`, but never gates request logic.
   *
   * @returns `{ token, source }` — `token` is null when no auth is available.
   */
  private async getAuthToken(): Promise<{ token: string | null; source: ProviderAPIAuthSource }> {
    this.lastAuthSource = null

    try {
      // Check for Epic launch JWT first
      const epicClient = EpicAuthClient.getInstance()
      const state = epicClient.getInstanceState()

      console.log('[ProviderAPIClient] Checking for Epic JWT with state:', state)
      console.log('[ProviderAPIClient] Has Epic session:', epicClient.hasSession(state))

      if (epicClient.hasSession(state)) {
        // Use Auth0-compatible JWT from epic-provision (find user by NPI, get permissions)
        const providerApiToken = await epicClient.getOrRefreshProviderApiToken(state)
        if (providerApiToken) {
          this.lastAuthSource = 'epic-provision'
          console.log('[ProviderAPIClient] Using epic-provision JWT for provider-api (X-Auth-Source: epic-provision)')
          return { token: providerApiToken, source: 'epic-provision' }
        }

        // Fallback: API key (backward compatibility)
        const context = epicClient.getContext(state)
        if (context.apiKey) {
          this.lastAuthSource = 'epic-api-key'
          console.log('[ProviderAPIClient] Using Epic API key for provider-api (X-Auth-Source: epic-api-key)')
          return { token: context.apiKey, source: 'epic-api-key' }
        }

        console.warn('[ProviderAPIClient] Epic session exists but no provider-api token (epic-provision or API key)')
      } else {
        console.log('[ProviderAPIClient] No Epic session found for state')
      }
    } catch (error) {
      // Epic auth client not available or error getting JWT
      console.error('[ProviderAPIClient] Error checking Epic JWT:', error)
    }

    // Fall back to Auth0 token (standalone mode)
    console.log('[ProviderAPIClient] Attempting to get Auth0 token...')
    if (this.getAccessToken) {
      try {
        const auth0Token = await this.getAccessToken()
        this.lastAuthSource = 'auth0'
        console.log('[ProviderAPIClient] Using Auth0 JWT token for provider-api (X-Auth-Source: auth0)')
        return { token: auth0Token, source: 'auth0' }
      } catch (error) {
        console.error('[ProviderAPIClient] Failed to get Auth0 token:', error)
        return { token: null, source: null }
      }
    }

    // No authentication available
    console.warn('[ProviderAPIClient] No authentication token available')
    return { token: null, source: null }
  }

  private async request(request: RequestStructure, withAuth: boolean = true): Promise<Response> {
    return Sentry.startSpan(
      {
        op: 'http.client',
        name: `${request.method} ${request.path}`,
      },
      async (span) => {
        span.setAttribute('http.method', request.method)
        span.setAttribute('http.url', `${ProviderAPIClient.baseURL}${request.path}`)
        span.setAttribute('with_auth', withAuth)

        const outgoingHeaders: Record<string, string> = request.headers || {}

        // Per-request auth source (captured from getAuthToken's return, NOT read
        // from the shared this.lastAuthSource after the await). The singleton +
        // plain-async request path lets concurrent requests interleave, so the
        // instance field can be overwritten by another request between our await
        // and our reads below; a local is the only value that is guaranteed to
        // describe THIS request's token.
        let authSource: ProviderAPIAuthSource = null

        // JWT Authentication: Auto-inject Bearer token for authenticated requests
        if (withAuth) {
          const { token: accessToken, source } = await this.getAuthToken()
          authSource = source

          if (accessToken) {
            outgoingHeaders['Authorization'] = `Bearer ${accessToken}`
            span.setAttribute('auth_method', 'jwt')
            // So you can see which token was sent: DevTools → Network → request headers
            if (authSource) {
              outgoingHeaders['X-Auth-Source'] = authSource
              span.setAttribute('auth_source', authSource)
            }
          } else {
            console.warn('[ProviderAPIClient] Auth requested but no token available')
            span.setAttribute('auth_error', true)
            // For auth-required endpoints, this will result in 401 from backend
            // which is the correct behavior
          }
        }

        // Properly handle request body serialization
        let processedBody: string | undefined
        if (request.body !== undefined && request.body !== null) {
          if (typeof request.body === 'string') {
            processedBody = request.body // Already a string, don't double-stringify
          } else {
            processedBody = JSON.stringify(request.body) // Stringify objects
          }
        }

        try {
          const response = await fetch(`${ProviderAPIClient.baseURL}${request.path}`, {
            method: request.method,
            headers: outgoingHeaders,
            body: processedBody,
            signal: request.signal,
          })

          span.setAttribute('http.status_code', response.status)

          // Bounded, non-looping 401 re-exchange for Epic sessions (§4.4). Only
          // when THIS request was authenticated with an epic-provision token (the
          // per-request authSource local, never the shared this.lastAuthSource): on
          // a 401, force EXACTLY ONE fresh server-side epic-provision re-exchange
          // and retry the original request ONCE. A second 401, or a re-exchange
          // that yields no token (e.g. epic-provision 503), is terminal → surface a
          // re-launch-from-Epic signal. epic-provision is never called more than
          // once per original 401.
          if (response.status === 401 && withAuth && authSource === 'epic-provision') {
            return await this.reExchangeAndRetry(request, outgoingHeaders, span, authSource)
          }

          return response
        } catch (error) {
          span.setAttribute('error', true)
          if (error instanceof Error) {
            span.setAttribute('error.message', error.message)
          }
          // AbortError is intentional (user navigated away, new search superseded old one, etc.).
          // Reporting it to Sentry creates noise without actionable signal.
          const isAbort = error instanceof Error && error.name === 'AbortError'
          if (!isAbort) {
            Sentry.captureException(error, {
              tags: {
                operation: 'http.client',
                method: request.method,
                path: request.path,
              },
            })
          }
          throw error
        }
      }
    )
  }

  /**
   * Bounded, single-shot 401 re-exchange for an Epic-provision session (§4.4).
   *
   * Forces EXACTLY ONE fresh server-side `epic-provision` re-exchange (via
   * {@link EpicAuthClient.getOrRefreshProviderApiToken} with `forceRefresh`) and
   * retries the original request ONCE with the fresh Bearer. This is server-side
   * re-exchange only — never in-iframe silent auth.
   *
   * Terminal (never looped): if the re-exchange yields no token (e.g. the
   * re-`epic-provision` returned 503) or the single retry still 401s, throws
   * {@link EpicReExchangeRequiredError} so the UI surfaces a re-launch-from-Epic
   * prompt. `epic-provision` is called at most once per original 401.
   */
  private async reExchangeAndRetry(
    request: RequestStructure,
    outgoingHeaders: Record<string, string>,
    span: { setAttribute(key: string, value: string | number | boolean): void },
    authSource: ProviderAPIAuthSource
  ): Promise<Response> {
    span.setAttribute('epic_reexchange_attempt', true)

    const epicClient = EpicAuthClient.getInstance()
    const state = epicClient.getInstanceState()

    // Exactly one forced re-exchange: re-run the server-side epic-provision.
    const freshToken = await epicClient.getOrRefreshProviderApiToken(state, { forceRefresh: true })
    if (!freshToken) {
      // No fresh token (e.g. epic-provision 503 / no Epic artifact) → terminal.
      span.setAttribute('epic_reexchange_result', 'no_token')
      throw new EpicReExchangeRequiredError()
    }

    const retryHeaders: Record<string, string> = { ...outgoingHeaders, Authorization: `Bearer ${freshToken}` }
    // Use the per-request source (this branch is only reached for an
    // epic-provision request, and the forced re-exchange re-mints an
    // epic-provision token) — not the shared this.lastAuthSource, which a
    // concurrent request may have overwritten.
    if (authSource) {
      retryHeaders['X-Auth-Source'] = authSource
    }

    let processedBody: string | undefined
    if (request.body !== undefined && request.body !== null) {
      processedBody = typeof request.body === 'string' ? request.body : JSON.stringify(request.body)
    }

    // Retry the ORIGINAL request exactly once with the fresh token.
    const retried = await fetch(`${ProviderAPIClient.baseURL}${request.path}`, {
      method: request.method,
      headers: retryHeaders,
      body: processedBody,
      signal: request.signal,
    })
    span.setAttribute('http.status_code', retried.status)

    if (retried.status === 401) {
      // A second 401 after a fresh re-exchange is terminal — do NOT loop.
      span.setAttribute('epic_reexchange_result', 'retry_401')
      throw new EpicReExchangeRequiredError()
    }

    span.setAttribute('epic_reexchange_result', 'retry_ok')
    return retried
  }

  public async get(
    path: string,
    headers?: Record<string, string>,
    withAuth: boolean = true,
    signal?: AbortSignal
  ): Promise<Response> {
    return this.request({ method: HTTPMethod.GET, path, headers, signal }, withAuth)
  }

  public async post(
    path: string,
    body?: string | object,
    headers?: Record<string, string>,
    withAuth: boolean = true,
    signal?: AbortSignal
  ): Promise<Response> {
    return this.request({ method: HTTPMethod.POST, path, body, headers, signal }, withAuth)
  }

  public async put(
    path: string,
    body?: string | object,
    headers?: Record<string, string>,
    withAuth: boolean = true,
    signal?: AbortSignal
  ): Promise<Response> {
    return this.request({ method: HTTPMethod.PUT, path, body, headers, signal }, withAuth)
  }

  public async patch(
    path: string,
    body?: string | object,
    headers?: Record<string, string>,
    withAuth: boolean = true,
    signal?: AbortSignal
  ): Promise<Response> {
    return this.request({ method: HTTPMethod.PATCH, path, body, headers, signal }, withAuth)
  }

  public async delete(
    path: string,
    headers?: Record<string, string>,
    withAuth: boolean = true,
    signal?: AbortSignal
  ): Promise<Response> {
    return this.request({ method: HTTPMethod.DELETE, path, headers, signal }, withAuth)
  }

  private static readonly jsonHeaders = { 'Content-Type': 'application/json' }

  /**
   * POST /ordering/sessions. With `restartFromOrderId` + `restartMode` the draft
   * restarts that cancelled order (GEN-6020); a 409 naming the patient's open
   * draft throws {@link RestartDraftExistsError}.
   */
  public async createOrderingSession(
    body: Record<string, unknown> & { restartFromOrderId?: string; restartMode?: 'keep' | 'reset' }
  ): Promise<Record<string, unknown>> {
    const res = await this.post('/ordering/sessions', body, ProviderAPIClient.jsonHeaders)
    if (!res.ok) {
      const text = await res.text()
      if (res.status === 409 && body.restartFromOrderId) {
        let draftId: unknown
        try {
          draftId = (JSON.parse(text) as { detail?: { existingDraftSessionId?: unknown } })?.detail
            ?.existingDraftSessionId
        } catch {
          draftId = undefined
        }
        if (typeof draftId === 'string' && draftId) {
          throw new RestartDraftExistsError(draftId)
        }
        throw new RestartConflictError()
      }
      throw new Error(`ordering session create failed: ${res.status} ${text}`)
    }
    const result = (await res.json()) as Record<string, unknown>
    this.rememberOrderingSessionVersion(result)
    return result
  }

  /**
   * PATCH /ordering/sessions/{sessionId}, sending the session's last known `updatedAt` as
   * `expectedUpdatedAt`.
   *
   * Writes to one session run one at a time, each with the version the previous one returned.
   * A write that has been sent is never cancelled, because the server may commit it anyway and
   * the next write needs its version: `signal` skips a write that has not been sent yet, and
   * makes the call reject with `AbortError` if it fires while the write is in flight.
   */
  public patchOrderingSession(
    sessionId: string,
    body: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<Record<string, unknown>> {
    const previous = this.orderingSessionWrites.get(sessionId) ?? Promise.resolve()
    const write = previous.then(() => this.sendOrderingSessionPatch(sessionId, body, signal))
    const settled = write.then(
      () => undefined,
      () => undefined
    )
    this.orderingSessionWrites.set(sessionId, settled)
    void settled.then(() => {
      if (this.orderingSessionWrites.get(sessionId) === settled) this.orderingSessionWrites.delete(sessionId)
    })
    return write
  }

  private async sendOrderingSessionPatch(
    sessionId: string,
    body: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<Record<string, unknown>> {
    if (signal?.aborted) throw abortError()
    let expectedUpdatedAt = this.orderingSessionVersions.get(sessionId)
    if (!expectedUpdatedAt) {
      const current = await this.getOrderingSession(sessionId, signal)
      expectedUpdatedAt = typeof current['updatedAt'] === 'string' ? current['updatedAt'] : undefined
    }
    if (!expectedUpdatedAt) throw new Error('ordering session patch requires an updated version')
    if (signal?.aborted) throw abortError()
    let res: Response
    try {
      res = await this.patch(
        `/ordering/sessions/${sessionId}`,
        { ...body, expectedUpdatedAt },
        ProviderAPIClient.jsonHeaders
      )
    } catch (error) {
      // No response, but the write may still have committed: re-read the version next time.
      this.orderingSessionVersions.delete(sessionId)
      throw error
    }
    if (res.status === 404) {
      throw new OrderingSessionGoneError('ordering session patch: session not found')
    }
    if (res.status === 409) {
      // Someone else wrote the session: the next call re-reads its version instead of replaying ours.
      this.orderingSessionVersions.delete(sessionId)
    }
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`ordering session patch failed: ${res.status} ${text}`)
    }
    const result = (await res.json()) as Record<string, unknown>
    this.rememberOrderingSessionVersion(result, sessionId)
    if (signal?.aborted) throw abortError()
    return result
  }

  /** DELETE /ordering/sessions/{sessionId} — hard-delete an in-progress draft (GEN-3272). */
  public async deleteOrderingSession(sessionId: string, signal?: AbortSignal): Promise<void> {
    const res = await this.delete(`/ordering/sessions/${sessionId}`, undefined, true, signal)
    if (res.status === 204 || res.status === 404) {
      return
    }
    if (res.status === 409) {
      const text = await res.text()
      let detail = text
      try {
        const parsed = JSON.parse(text) as { detail?: string }
        if (typeof parsed.detail === 'string') detail = parsed.detail
      } catch {
        // use raw text
      }
      throw new OrderingSessionDiscardConflictError(parseDiscardConflictKind(detail), detail)
    }
    if (res.status >= 500) {
      const text = await res.text()
      throw new Error(`Could not discard the order (${res.status}). ${text}`.trim())
    }
    const text = await res.text()
    throw new Error(`ordering session delete failed: ${res.status} ${text}`)
  }

  /**
   * POST /ordering/sessions/{sessionId}/draft-pdf — generate (or reuse) the DRAFT TRF PDF
   * server-side and return a short-lived pre-signed GET URL (no PDF bytes in the body).
   *
   * The backend renders from the persisted session ``state.canonicalOrder``, so callers must
   * PATCH the current (merged) canonical into the session before invoking this (GEN-4159 / C-1).
   */
  public async createDraftPdf(
    sessionId: string,
    body?: { isEhrOrder?: boolean },
    signal?: AbortSignal
  ): Promise<{ pdfUrl: string; expiresIn: number; regenerated: boolean }> {
    const res = await this.post(
      `/ordering/sessions/${sessionId}/draft-pdf`,
      body ?? {},
      ProviderAPIClient.jsonHeaders,
      true,
      signal
    )
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`draft PDF request failed: ${res.status} ${text}`)
    }
    return (await res.json()) as { pdfUrl: string; expiresIn: number; regenerated: boolean }
  }

  /**
   * POST /database/orders/{orderId}/regenerate-pdf — re-render the TRF PDF for an already-placed
   * order server-side from the canonical passed in the body, returning a short-lived pre-signed
   * GET URL (no PDF bytes in the body).
   *
   * Unlike the stored submit-time artifact exposed on order-details (`trf_pdf_url`), this renders
   * from the supplied (possibly unsent) canonical, so the in-progress "View Order Form" reflects
   * edits the provider has made but not yet sent. The result is transient — it does not overwrite
   * the order's persisted artifact.
   */
  public async regenerateOrderPdf(
    orderId: string,
    body: { canonicalOrder: CanonicalOrderPayload; isEhrOrder?: boolean },
    caller: string,
    signal?: AbortSignal
  ): Promise<{ pdfUrl: string; expiresIn: number }> {
    const res = await this.post(
      `/database/orders/${orderId}/regenerate-pdf?caller=${encodeURIComponent(caller)}`,
      body,
      ProviderAPIClient.jsonHeaders,
      true,
      signal
    )
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`regenerate order PDF request failed: ${res.status} ${text}`)
    }
    return (await res.json()) as { pdfUrl: string; expiresIn: number }
  }

  /**
   * GET /ordering/sessions/{sessionId}/draft-pdf/content — generate (or reuse) the DRAFT TRF
   * PDF server-side and stream the bytes back same-origin.
   *
   * Same render as {@link createDraftPdf}, but returns the PDF bytes directly instead of a
   * pre-signed S3 URL. This is what makes the TRF render inside the Epic SMART embedded iframe:
   * a direct browser→S3 fetch of the pre-signed URL is blocked cross-origin there, whereas this
   * response is same-origin (JWT-authenticated through provider-api), mirroring the report and
   * shipping-label byte-streaming endpoints.
   */
  public async getDraftPdfContent(
    sessionId: string,
    body?: { isEhrOrder?: boolean },
    signal?: AbortSignal
  ): Promise<TrfPdf> {
    const q = body?.isEhrOrder != null ? `?isEhrOrder=${body.isEhrOrder ? 'true' : 'false'}` : ''
    const res = await this.get(`/ordering/sessions/${sessionId}/draft-pdf/content${q}`, undefined, true, signal)
    if (!res.ok) {
      throw new Error(`draft PDF content request failed: ${res.status}`)
    }
    return ProviderAPIClient.readPdfBytes(res)
  }

  /**
   * GET /database/orders/{orderId}/trf-pdf/content — stream a placed order's stored TRF bytes
   * same-origin (Epic-iframe safe).
   *
   * Returns ``null`` when the backend reports the order has no stored artifact (404), so callers
   * fall back to the client-side render — an expected state for orders placed before server-side
   * generation shipped, not a failure. Any other non-2xx throws.
   */
  public async getOrderTrfPdfContent(orderId: string, caller: string, signal?: AbortSignal): Promise<TrfPdf | null> {
    const res = await this.get(
      `/database/orders/${orderId}/trf-pdf/content?caller=${encodeURIComponent(caller)}`,
      undefined,
      true,
      signal
    )
    if (res.status === 404) {
      return null
    }
    if (!res.ok) {
      throw new Error(`order TRF PDF content request failed: ${res.status}`)
    }
    return ProviderAPIClient.readPdfBytes(res)
  }

  /**
   * POST /database/orders/{orderId}/regenerate-pdf/content — re-render the TRF from the supplied
   * (possibly unsent) canonical and stream the bytes back same-origin (Epic-iframe safe).
   *
   * Same transient render as {@link regenerateOrderPdf}, but returns the PDF bytes directly.
   */
  public async regenerateOrderPdfContent(
    orderId: string,
    body: { canonicalOrder: CanonicalOrderPayload; isEhrOrder?: boolean },
    caller: string,
    signal?: AbortSignal
  ): Promise<TrfPdf> {
    const res = await this.post(
      `/database/orders/${orderId}/regenerate-pdf/content?caller=${encodeURIComponent(caller)}`,
      body,
      ProviderAPIClient.jsonHeaders,
      true,
      signal
    )
    if (!res.ok) {
      throw new Error(`regenerate order PDF content request failed: ${res.status}`)
    }
    return ProviderAPIClient.readPdfBytes(res)
  }

  /**
   * Validate a PDF byte response and return its bytes plus the server-chosen filename (GEN-5844).
   *
   * Guards against a non-PDF body (e.g. an error page) reaching the PDF viewer: the response must
   * carry an ``application/pdf`` content type and the bytes must begin with the ``%PDF`` magic
   * header (mirrors the report client's validation).
   */
  private static async readPdfBytes(res: Response): Promise<TrfPdf> {
    const contentType = res.headers.get('content-type')
    if (!contentType || !contentType.includes('application/pdf')) {
      throw new Error('Response is not a PDF file')
    }
    const bytes = new Uint8Array(await res.arrayBuffer())
    if (bytes.length < 4 || String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]) !== '%PDF') {
      throw new Error('Response is not a valid PDF file')
    }
    const filename = /filename="([A-Za-z0-9_-]+\.pdf)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? null
    return { bytes, filename }
  }

  /**
   * POST /ordering/sessions/{sessionId}/submit
   *
   * `launchMode` records where the order was submitted FROM (not where its draft was
   * created), which provider-api maps to `order_item.submitted_from`
   * (`ehr` / `provider_app`). It is resolved at submit time from the caller's launch flow.
   * Omitting it leaves the column `unknown`; provider-api deliberately does not guess from
   * the session's frozen launch mode or `ehrContext`, since those answer "created via EHR"
   * instead.
   */
  public async submitOrderingSession(
    sessionId: string,
    body: Record<string, unknown>,
    options?: { submitViaRedox?: boolean; launchMode?: LaunchMode }
  ): Promise<Record<string, unknown>> {
    const params = new URLSearchParams()
    if (options?.submitViaRedox) params.set('submitViaRedox', 'true')
    if (options?.launchMode) params.set('launchMode', options.launchMode)
    const q = params.toString() ? `?${params.toString()}` : ''
    const res = await this.post(`/ordering/sessions/${sessionId}/submit${q}`, body, ProviderAPIClient.jsonHeaders)
    if (res.status === 409) {
      const text = await res.text()
      // Submit answers 409 for two unrelated situations: this session already produced
      // an order, or another request holds the row lock mid-submit. Only the first is
      // terminal, so the recoverable lock case keeps falling through to the generic
      // error and stays retryable. Matched on the full phrase rather than "already" so
      // a transient conflict worded "already in progress" cannot read as terminal.
      if (text.toLowerCase().includes('already been submitted')) {
        throw new OrderingSessionAlreadySubmittedError()
      }
      throw new Error(`ordering session submit failed: ${res.status} ${text}`)
    }
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`ordering session submit failed: ${res.status} ${text}`)
    }
    return (await res.json()) as Record<string, unknown>
  }

  /**
   * POST /ordering/sessions/{sessionId}/coverage-check — advisory insurance-coverage
   * eligibility check for the draft. Never blocks and never submits; returns per-dimension
   * eligibility so the UI can decide whether to warn. Returns ``null`` (fail-open) on any
   * non-OK response (403/404) or network error, since the check is purely advisory and the
   * server no longer enforces coverage at submit time.
   *
   * The body is optional — POST with no ``ehrAccessToken`` when none is available.
   */
  public async coverageCheck(
    sessionId: string,
    body?: CoverageCheckRequest,
    signal?: AbortSignal
  ): Promise<CoverageCheckResponse | null> {
    try {
      const res = await this.post(
        `/ordering/sessions/${sessionId}/coverage-check`,
        body ?? {},
        ProviderAPIClient.jsonHeaders,
        true,
        signal
      )
      if (!res.ok) {
        Sentry.logger.warn('ordering.coverage_check.non_ok', { sessionId, status: res.status })
        return null
      }
      return (await res.json()) as CoverageCheckResponse
    } catch (e) {
      // Swallow aborts (encounter/provider changed mid-flight); report the rest.
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        Sentry.captureException(e, { tags: { operation: 'ordering.coverage_check' } })
      }
      return null
    }
  }

  /** GET /ordering/sessions/{sessionId} — fetch current session state. */
  public async getOrderingSession(sessionId: string, signal?: AbortSignal): Promise<Record<string, unknown>> {
    const res = await this.get(`/ordering/sessions/${sessionId}`, undefined, true, signal)
    if (res.status === 404) {
      throw new OrderingSessionGoneError('ordering session fetch: session not found')
    }
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`ordering session fetch failed: ${res.status} ${text}`)
    }
    const result = (await res.json()) as Record<string, unknown>
    this.rememberOrderingSessionVersion(result, sessionId)
    return result
  }

  /**
   * GET /ordering/sessions/exists?patientId= — lightweight preflight check for an
   * in-progress ordering session. Always returns 200 with { exists: boolean } for
   * authorized requests, avoiding the 404 that GET /ordering/sessions produces on miss.
   */
  public async orderingSessionExists(patientId: string): Promise<boolean> {
    const q = encodeURIComponent(patientId)
    const res = await this.get(`/ordering/sessions/exists?patientId=${q}`)
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`ordering session exists check failed: ${res.status} ${text}`)
    }
    const body = (await res.json()) as { exists?: boolean }
    return body?.exists === true
  }

  /** GET /ordering/sessions?patientId= — resume in-progress draft for patient (404 → null). */
  public async getOrderingSessionByPatient(patientId: string): Promise<Record<string, unknown> | null> {
    const q = encodeURIComponent(patientId)
    const res = await this.get(`/ordering/sessions?patientId=${q}`)
    if (res.status === 404) {
      return null
    }
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`ordering session lookup by patient failed: ${res.status} ${text}`)
    }
    const result = (await res.json()) as Record<string, unknown>
    this.rememberOrderingSessionVersion(result)
    return result
  }

  private rememberOrderingSessionVersion(result: Record<string, unknown>, fallbackSessionId?: string): void {
    const sessionId = typeof result['sessionId'] === 'string' ? result['sessionId'] : fallbackSessionId
    const updatedAt = result['updatedAt']
    if (sessionId && typeof updatedAt === 'string') this.orderingSessionVersions.set(sessionId, updatedAt)
  }

  /** POST /ordering/sessions/{sessionId}/validate — dry-run mapper + org resolution (no IMDB upsert). */
  public async validateOrderingSession(
    sessionId: string,
    body: Record<string, unknown>
  ): Promise<{ valid: boolean; errors: string[] }> {
    const res = await this.post(`/ordering/sessions/${sessionId}/validate`, body, ProviderAPIClient.jsonHeaders)
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`ordering session validate failed: ${res.status} ${text}`)
    }
    return (await res.json()) as { valid: boolean; errors: string[] }
  }

  /** GET /geo/suggestions?query= — address autosuggestions (aborts supersede). */
  public async getAddressSuggestions(query: string, signal?: AbortSignal): Promise<AddressSuggestion[]> {
    const q = encodeURIComponent(query)
    const res = await this.get(`/geo/suggestions?query=${q}`, undefined, true, signal)
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`address suggestions failed: ${res.status} ${text}`)
    }
    const body = (await res.json()) as { suggestions?: AddressSuggestion[] }
    return body?.suggestions ?? []
  }

  /** GET /geo/place/{placeId} — resolve a suggestion to a structured address. */
  public async resolveAddress(placeId: string, signal?: AbortSignal): Promise<ResolvedAddress> {
    const res = await this.get(`/geo/place/${encodeURIComponent(placeId)}`, undefined, true, signal)
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`address resolve failed: ${res.status} ${text}`)
    }
    return (await res.json()) as ResolvedAddress
  }

  /**
   * POST /orders/{orderId}/trf/signing — mint a fresh, single-use DocuSign
   * recipient-view URL for the order's TRF (GEN-5028; endpoint GEN-5026).
   *
   * Authorization is enforced server-side: only the clinician of record may
   * obtain a URL, the order must be on the embedded channel, and its signature
   * must still be signable. The returned URL is a short-lived (~300s) capability
   * token — open it on click and never cache, persist, or log it.
   */
  public async createTrfSigningUrl(orderId: string): Promise<{ url: string }> {
    let res: Response
    try {
      res = await this.post(`/orders/${encodeURIComponent(orderId)}/trf/signing`, {}, ProviderAPIClient.jsonHeaders)
    } catch (e) {
      // No response at all (offline, DNS, abort). Reported with a null status so
      // the caller treats it the same as a transient server refusal.
      throw new TrfSigningUrlError(null, e instanceof Error ? e.message : String(e))
    }
    if (!res.ok) {
      const text = await res.text()
      throw new TrfSigningUrlError(res.status, text)
    }
    return (await res.json()) as { url: string }
  }

  /**
   * GET /orders/{orderId}/trf/signer — whether a mint by this session would pass
   * authorization, decided by provider-api exactly as the mint decides it, and
   * when it would not, the login email of the clinician of record's account.
   *
   * Mints nothing and returns nothing of the order, so it is safe to ask before
   * the page has proved anything. Throws on any non-ok response.
   */
  public async getTrfSigner(orderId: string): Promise<TrfSigner> {
    const res = await this.get(`/orders/${encodeURIComponent(orderId)}/trf/signer`, ProviderAPIClient.jsonHeaders)
    if (!res.ok) {
      throw new Error(`TRF signer check failed: ${res.status}`)
    }
    const body = (await res.json()) as { isSigner?: unknown; signerEmail?: unknown }
    return {
      isSigner: body.isSigner === true,
      signerEmail: typeof body.signerEmail === 'string' && body.signerEmail.trim() ? body.signerEmail.trim() : null,
    }
  }

  /**
   * POST /orders/{orderId}/trf/signature-reminder — send the embedded-signing
   * reminder email immediately, consuming the per-envelope reminder claim so the
   * abandonment watcher does not send a second mail.
   *
   * Throws {@link SignatureReminderRefusedError} on 409, classified from the
   * response so the caller knows whether to wait and ask again. Any other non-ok
   * response throws a generic Error.
   */
  public async sendTrfSignatureReminder(orderId: string): Promise<void> {
    const res = await this.post(
      `/orders/${encodeURIComponent(orderId)}/trf/signature-reminder`,
      {},
      ProviderAPIClient.jsonHeaders
    )
    if (res.status === 409) {
      // An unreadable body is not a reason to guess: the classifier's default
      // is the retryable one, which is also the safe one to be wrong about.
      const detail = await res
        .json()
        .then((body: unknown) => (body as { detail?: unknown } | null)?.detail)
        .catch(() => undefined)
      throw new SignatureReminderRefusedError(
        reminderRefusalFromDetail(typeof detail === 'string' ? detail : undefined)
      )
    }
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`TRF signature reminder failed: ${res.status} ${text}`)
    }
  }
}

export default ProviderAPIClient
